import axios from 'axios';
import { API_BASE_URL } from '../config';
// import { logout } from './authService'; // Removed to fix circular dependency

// Debug log removed // Debug log

let api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
  // withCredentials: true, // removed, do not send cookies
});

let isLoggingOut = false;
let userUpdateTimeout = null; // Debounce timer for user data updates

// Efficient user data comparison function (shared with AuthContext)
const hasUserDataChanged = (currentUser, newUser) => {
  if (!currentUser || !newUser) return true;
  
  // Compare critical fields that affect app functionality
  const criticalFields = [
    'user_id', 'user_full_name', 'user_email', 'role', 'department',
    'permissions', 'user_permissions', 'approver', 'supervisor', 
    'manager', 'hod', 'director', 'role_name'
  ];
  
  for (const field of criticalFields) {
    const currentValue = JSON.stringify(currentUser[field]);
    const newValue = JSON.stringify(newUser[field]);
    if (currentValue !== newValue) {
      return true;
    }
  }
  return false;
};

const setupInterceptors = () => {
  // Add auth token to requests if available
  api.interceptors.request.use(config => {
    // MMS Mobile endpoints use their own token system
    if (config.url && (config.url.includes('/api/mms/mobile/') || config.url.includes('/api/mms/assignments/') || config.url.includes('/api/mms/activities/'))) {
      // For login and verify_otp, don't send any token
      if (!config.url.includes('/login') && !config.url.includes('/verify_otp')) {
        // For other MMS mobile endpoints, use mms_mobile_token
        const mmsToken = localStorage.getItem('mms_mobile_token');
        if (mmsToken) {
          config.headers.Authorization = `Bearer ${mmsToken}`;
        }
      }
    } else {
      // Only use regular user tokens for the main API
      // Supplier tokens should only be used by supplierApi.js
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    // Handle FormData requests - remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // Add masquerading information if active - use query parameters instead of headers to avoid CORS issues
    // BUT: If X-Masquerade-User header is explicitly set, use that instead (for buyer dashboard feature)
    const hasExplicitMasqueradeHeader = config.headers && config.headers['X-Masquerade-User'];
    
    if (!hasExplicitMasqueradeHeader) {
      const masqueradeData = localStorage.getItem('masquerading');
      if (masqueradeData) {
        try {
          const masqueradeState = JSON.parse(masqueradeData);
          if (masqueradeState.isMasquerading && masqueradeState.targetUser) {
            // Handle nested user data structure
            let targetUser = masqueradeState.targetUser;
            if (targetUser && targetUser.row) {
              targetUser = targetUser.row;
            }
            
            // Add masquerading data as query parameters
            const masqueradeParams = {
              masquerade_user_id: targetUser.user_id,
              masquerade_department: targetUser.department,
              masquerade_role: targetUser.role
            };
            
            // Add to URL params - handle both URLSearchParams and plain objects
            if (config.params) {
              if (config.params instanceof URLSearchParams) {
                // If params is URLSearchParams, append to it
                Object.keys(masqueradeParams).forEach(key => {
                  config.params.append(key, masqueradeParams[key]);
                });
              } else {
                // If params is plain object, merge with spread
                config.params = { ...config.params, ...masqueradeParams };
              }
            } else {
              config.params = masqueradeParams;
            }
          }
        } catch (error) {
          console.error('Error parsing masquerading data for API request:', error);
        }
      }
    } else {
      // When X-Masquerade-User header is set, also add as query params for backend compatibility
      try {
        const masqueradeHeader = JSON.parse(config.headers['X-Masquerade-User']);
        const masqueradeParams = {
          masquerade_user_id: masqueradeHeader.user_id,
          masquerade_department: masqueradeHeader.department,
          masquerade_role: masqueradeHeader.role
        };
        
        // Add to URL params - handle both URLSearchParams and plain objects
        if (config.params) {
          if (config.params instanceof URLSearchParams) {
            // If params is URLSearchParams, append to it
            Object.keys(masqueradeParams).forEach(key => {
              config.params.append(key, masqueradeParams[key]);
            });
          } else {
            // If params is plain object, merge with spread
            config.params = { ...config.params, ...masqueradeParams };
          }
        } else {
          config.params = masqueradeParams;
        }
      } catch (error) {
        console.error('Error parsing X-Masquerade-User header:', error);
      }
    }
    
    // Ensure all requests include /api/ prefix
    if (config.url && !config.url.startsWith('/api/') && !config.url.startsWith('http')) {
      config.url = '/api/' + config.url;
      // Debug log removed
    }
    

    
    return config;
  });

  // Add a response interceptor to update user data if server sends fresh data
  api.interceptors.response.use(
    response => {
      // Check if the response includes updated user data
      // This happens when check_session or other auth endpoints return user info
      if (response.data && response.data.user) {
        const currentUserStr = localStorage.getItem('user');
        if (currentUserStr) {
          try {
            const currentUser = JSON.parse(currentUserStr);
            const newUser = response.data.user;
            
            // Efficient comparison of critical fields only
            const hasChanged = hasUserDataChanged(currentUser, newUser);
            
            if (hasChanged) {
              console.log('🔄 API Interceptor: User data changed, debouncing update');
              
              // Debounce user data updates to prevent rapid successive updates
              clearTimeout(userUpdateTimeout);
              userUpdateTimeout = setTimeout(() => {
                localStorage.setItem('user', JSON.stringify(newUser));
                
                // Dispatch custom event to notify AuthContext of the change
                // This allows immediate state update without waiting for periodic refresh
                window.dispatchEvent(new CustomEvent('userDataChanged', { 
                  detail: { user: newUser } 
                }));
              }, 500); // Debounce for 500ms
            }
          } catch (err) {
            console.error('Error checking for user data changes:', err);
          }
        }
      }
      return response;
    },
    error => {
      if (!isLoggingOut && error.response && error.response.status === 401) {
        const originalRequestUrl = error.config.url;
        
        // Don't redirect on 401 for session check, login, OTP verification, approval token, posts, logs, supplier endpoints, or MMS mobile
        if (originalRequestUrl.endsWith('/api/check_session') || 
            originalRequestUrl.endsWith('/api/auth_login') ||
            originalRequestUrl.endsWith('/api/auth_approval_token') ||
            originalRequestUrl.endsWith('/api/verify_otp') ||
            originalRequestUrl.endsWith('/api/resend_otp') ||
            originalRequestUrl.includes('/api/posts') ||
            originalRequestUrl.includes('/api/categories') ||
            originalRequestUrl.includes('/api/logs') ||
            originalRequestUrl.includes('/api/supplier/') ||
            originalRequestUrl.includes('/api/mms/mobile/') ||
            originalRequestUrl.includes('/api/mms/assignments/') ||
            originalRequestUrl.includes('/api/mms/activities/') ||
            originalRequestUrl.includes('/api/helpdesk/rate')) {
          console.log('🚫 401 interceptor: Skipping logout for', originalRequestUrl);
          return Promise.reject(error); // Reject the promise to allow the caller to handle it
        }
        
        console.log('🚪 401 interceptor: Calling logout for', originalRequestUrl);
        
        // For all other 401s, redirect to login
        // logout(); // Clear user data and redirect
        // Dispatch event to break circular dependency with authService
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
      return Promise.reject(error);
    }
  );
};

setupInterceptors();

export const setLoggingOut = (status) => {
  isLoggingOut = status;
};

export const reinitializeApi = () => {
  // Debug log removed
  api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json'
    }
    // withCredentials: true, // removed
  });
  setupInterceptors();
};

// Reinitialize API to pick up any config changes
reinitializeApi();

export default api;
